import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { MwcIconService } from 'mwc/icon';
import { CommonService } from 'src/app/services/common/common.service';
import { TokenService } from 'src/app/services/token/token.service';
import { OnboardingService } from 'src/app/services/onboarding/onboarding.service';
import { ModeratorRedirectService } from 'src/app/services/moderation/moderator-redirect.service';
import { StorageHelper } from 'src/app/_utilities/storage-helper';
import { firstValueFrom } from 'rxjs';
import { VerifyUserRequest } from '../models/common';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  private _storageHelper = new StorageHelper();

  constructor(
    private iconService: MwcIconService,
    private commonService: CommonService,
    private tokenService: TokenService,
    private onboardingService: OnboardingService,
    private moderatorRedirectService: ModeratorRedirectService,
    private router: Router
  ) { }

  private async ensureUserInfoLoaded(): Promise<void> {
    const userInfo = await firstValueFrom(this.commonService.getUserInfoValue());
    if (!userInfo) {
      await firstValueFrom(this.commonService.fetchUserInfo());
    }
  }

  async canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    try {
      const isAuthenticated = await firstValueFrom(this.tokenService.isAuthenticated$);
      const hasValidToken = this.tokenService.getAccessToken() && !this.tokenService.isTokenExpired();
      if (isAuthenticated && hasValidToken) {
        await this.ensureUserInfoLoaded();
        await this.tokenService.getMixpanelInfo();
        this.loadIcons();
        const moderatorRedirect = this.moderatorRedirectService.moderatorRedirect(state.url);
        if (moderatorRedirect) return moderatorRedirect;
        const redirect = await this.checkOnboardingRedirect(state.url);
        if (redirect) return redirect;
        return true;
      }
      if (isAuthenticated && this.tokenService.isTokenExpired()) {
        const refreshResult = await firstValueFrom(this.tokenService.refreshToken());
        if (refreshResult?.access_token) {
          await this.ensureUserInfoLoaded();
          await this.tokenService.getMixpanelInfo();
          this.loadIcons();
          const moderatorRedirect = this.moderatorRedirectService.moderatorRedirect(state.url);
          if (moderatorRedirect) return moderatorRedirect;
          const redirect = await this.checkOnboardingRedirect(state.url);
          if (redirect) return redirect;
          return true;
        }
      }
      const user = await this.tryFetchToken();
      if (user?.accessToken) {
        await firstValueFrom(this.commonService.fetchUserInfo());
        this.loadIcons();
        const moderatorRedirect = this.moderatorRedirectService.moderatorRedirect(state.url);
        if (moderatorRedirect) return moderatorRedirect;
        const redirect = await this.checkOnboardingRedirect(state.url);
        if (redirect) return redirect;
        return true;
      }
      this.handleAuthenticationFailure(user);
      return false;
    } catch (error) {
      console.error('Auth guard error:', error);
      this.handleAuthenticationError(error);
      return false;
    }
  }

  /**
   * canActivate on a parent route does not re-run when navigating between its children, so this
   * keeps a Moderator from moving out of the article editor within manage-content (e.g. to the
   * article list) — they go to MPC approval management instead. The session is already
   * established by canActivate here.
   */
  canActivateChild(_childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.moderatorRedirectService.moderatorRedirect(state.url) ?? true;
  }

  private async checkOnboardingRedirect(targetUrl: string): Promise<UrlTree | null> {
    if (this.onboardingService.hasRedirected || targetUrl.includes('whats-new') || targetUrl.includes('/manage-content/articles/create-detailed-post/') || targetUrl.includes('/target-audience') || targetUrl.includes('/brand-voice') )  {
      return null;
    }
    if (this._storageHelper.user.contentGenerationId != null &&
      this._storageHelper.user.contentGenerationId != "") {
      this.onboardingService.markAsRedirected();
      return null;
      }
    const status = await firstValueFrom(this.onboardingService.getStatus(this._storageHelper.user.pId));
    if (status?.showOnboarding) {
      this.onboardingService.markAsRedirected();
      return this.router.createUrlTree(['/whats-new']);
    }
    return null;
  }

  private async tryFetchToken() {
    try {
      const request = new VerifyUserRequest();
      request.businessId = +this._storageHelper.user.bId;
      request.profileAlias = this._storageHelper.user.profileAlias;
      const user = await this.tokenService.fetchTokenFromApi(request);
      await this.tokenService.getMixpanelInfo();
      return user;
    } catch (error) {
      console.error('Failed to fetch token from API:', error);
      return null;
    }
  }

  private handleAuthenticationFailure(user: any): void {
    // Session could not be (re)established -> show the in-app session-expired screen
    // instead of bouncing straight to the external login.
    this.tokenService.routeToSessionExpired();
  }

  private handleAuthenticationError(error: any): void {
    this.tokenService.routeToSessionExpired();
  }

  private loadIcons(): void {
    this.iconService.fetchFromIconfont({ scriptUrl: 'assets/fonts.js' });
  }
}
